import { LinearGradient } from 'expo-linear-gradient';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  AlarmClock,
  ArrowLeft,
  Bell,
  BellOff,
  Box,
  Calendar,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Home as HomeIcon,
  Info,
  Lightbulb,
  ListChecks,
  Pencil,
  RotateCcw,
  ShoppingCart,
  SkipForward,
  Trash2,
} from 'lucide-react-native';
import { ComponentType, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { getSubtypeMeta, getSystemMeta } from '@/data/systems';
import {
  type ActiveTask,
  type TaskHistoryEntry,
  completeTask,
  deleteTask,
  dueLabel,
  editTaskOverride,
  getActiveProperty,
  getActiveTasks,
  getTaskById,
  getTaskHistory,
  skipTask,
  snoozeTask,
  uncompleteTask,
} from '@/lib/tasks';
import { TaskEditModal } from '@/components/TaskEditModal';
import { useConfirm, useNotice } from '@/components/ConfirmDialog';
import { ReschedulePicker } from '@/components/home/ReschedulePicker';
import { StickyBottomBar } from '@/components/StickyBottomBar';
import { goBackOrTo, goTo } from '@/lib/nav';
import {
  NOTIFICATIONS_AVAILABLE,
  WEB_NOTIFICATIONS_AVAILABLE,
  cancelTaskReminder,
  getScheduledTaskReminder,
  scheduleTaskReminder,
} from '@/lib/notifications';
import { getRuleByKey } from '@/lib/plan';
import { resolveIcon } from '@/lib/rule-icon';

const TIER_LABEL: Record<string, string> = {
  weekly: 'Weekly habit',
  monthly: 'Monthly habit',
  quarterly: 'Every few months',
  biannual: 'Twice a year',
  annual: 'Once a year',
  'multi-year': 'Long-term project',
};

/** Session-scoped task cache (Wave 70) — Prev/Next paging paints the
 *  next task INSTANTLY from here while the fresh DB read (still issued)
 *  reconciles underneath. Cleared naturally on app restart. */
const taskPageCache = new Map<string, ActiveTask>();

export default function TaskDetail() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const notice = useNotice();
  const [task, setTask] = useState<ActiveTask | null>(null);
  const [working, setWorking] = useState(false);
  const [reminderLead, setReminderLead] = useState<0 | 1 | 7>(0);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  // Edit modal — opened from the topbar pencil button. Lets the user
  // override title / time / difficulty on a predefined task. The save
  // marks the task is_custom=1 so future Replans preserve the edit.
  const [editing, setEditing] = useState(false);
  // Chronological siblings — used by the prev/next nav at the top of
  // the page. Loaded once per task switch from the same active-tasks
  // query the Home + Schedule pages use, so the order matches.
  const [siblings, setSiblings] = useState<{ id: string; title: string }[]>([]);

  // Load the chronological neighbor list ONCE per mount (Wave 70 —
  // this used to re-run the 500-row active-tasks query on EVERY
  // Prev/Next click, a big slice of the paging jank). The list only
  // actually changes when a task is completed/skipped, and those
  // actions navigate away anyway; a stale order for one session of
  // paging is harmless.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const prop = await getActiveProperty(db);
      if (!prop || cancelled) return;
      const list = await getActiveTasks(db, prop.id, 500);
      if (cancelled) return;
      setSiblings(list.map((t) => ({ id: t.id, title: t.title })));
    })();
    return () => {
      cancelled = true;
    };
  }, [db]);

  // Resolve the current task's position in the sibling list. -1 means
  // "not in the active list" — e.g. the user opened a completed task
  // from /timeline; in that case both prev and next are disabled.
  const currentSiblingIdx = siblings.findIndex((s) => s.id === id);
  const prevSibling = currentSiblingIdx > 0 ? siblings[currentSiblingIdx - 1] : null;
  const nextSibling =
    currentSiblingIdx >= 0 && currentSiblingIdx < siblings.length - 1
      ? siblings[currentSiblingIdx + 1]
      : null;

  // Prefetch the neighbors into the paging cache (Wave 70) so the next
  // Prev/Next click paints without waiting on SQLite at all.
  useEffect(() => {
    for (const s of [prevSibling, nextSibling]) {
      if (s && !taskPageCache.has(s.id)) {
        void getTaskById(db, s.id).then((t) => {
          if (t) taskPageCache.set(t.id, t);
        });
      }
    }
  }, [db, prevSibling, nextSibling]);

  // Snap back to the top when paging — carrying the old scroll offset
  // into a different task read as part of the "choppy" swap.
  const pageScrollRef = useRef<ScrollView>(null);
  useEffect(() => {
    pageScrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [id]);

  // Keyboard arrows on web — left/right navigate when the user hasn't
  // focused an input. Cheap navigation polish that matches how reader
  // UIs feel.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const onKey = (e: KeyboardEvent) => {
      // Ignore when user is typing into a contentEditable / input
      // (e.g. opened the reminder picker or focused a notes field).
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
      if (e.key === 'ArrowLeft' && prevSibling) {
        router.replace(`/task/${prevSibling.id}` as never);
      } else if (e.key === 'ArrowRight' && nextSibling) {
        router.replace(`/task/${nextSibling.id}` as never);
      }
    };
    // eslint-disable-next-line no-undef
    window.addEventListener('keydown', onKey);
    return () => {
      // eslint-disable-next-line no-undef
      window.removeEventListener('keydown', onKey);
    };
  }, [prevSibling, nextSibling, router]);

  // Past outcomes of this task's rule (Wave 64): every recorded done /
  // skip across the years, so the user can see whether they actually
  // did it last season or let it slide.
  const [history, setHistory] = useState<TaskHistoryEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    // Instant paint from the paging cache (Wave 70): the swap happens
    // this frame; the DB read below reconciles silently. History
    // clears so a stale record never shows under the wrong task.
    const cached = id ? taskPageCache.get(id) : undefined;
    if (cached) {
      setTask(cached);
      setHistory([]);
    }
    (async () => {
      if (!id) return;
      const t = await getTaskById(db, id);
      if (cancelled) return;
      setTask(t);
      if (t) {
        taskPageCache.set(t.id, t);
        const h = await getTaskHistory(db, t);
        if (!cancelled) setHistory(h);
      }
      // Prefer the persisted column (works on all platforms) over the
      // native-only OS-scheduled-notification lookup. The column is
      // kept in sync by onReminderPick below; if it's set, that's the
      // current intent regardless of whether the OS notif is still
      // scheduled.
      if (t?.reminder_lead_days === 1 || t?.reminder_lead_days === 7) {
        setReminderLead(t.reminder_lead_days);
      } else if (t?.due_at != null) {
        const existing = await getScheduledTaskReminder(t.id, t.due_at);
        if (!cancelled) setReminderLead(existing?.leadDays ?? 0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [db, id]);

  if (!task) {
    return <View style={styles.container} />;
  }

  // For seeded rules: pull rich content from task-rules.json.
  // For custom tasks: fall back to JSON fields the user entered.
  const seedRule = task.rule_key && !task.is_custom ? getRuleByKey(task.rule_key) : null;
  const parseJson = (s: string | null): string[] => {
    if (!s) return [];
    try {
      const arr = JSON.parse(s);
      return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
    } catch {
      return [];
    }
  };
  const rule = seedRule ?? {
    supplies: parseJson(task.supplies_json),
    steps: parseJson(task.steps_json),
    tips: parseJson(task.tips_json),
  };
  const Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }> =
    resolveIcon(task.icon ?? seedRule?.icon ?? null);
  const system = getSystemMeta(task.system_kind);
  const subtype = getSubtypeMeta(task.system_kind, task.system_subtype);

  // True when the task is already completed — we'll swap the action bar and
  // styling to reflect that, rather than offering Mark-Done again.
  const done = task.completed_at != null;

  const onMarkDone = async () => {
    // Cost-prompt gate, two layers (Wave 72.1):
    //   1. Preference master switch — off means never ask.
    //   2. Smart skip — habitual freebie chores (weekly cadence, or
    //      easy + ≤15 min) basically never have a cost, and asking
    //      "what did this cost?" after wiping a lint trap was the real
    //      annoyance behind "should this default off?". Service-shaped
    //      tasks still get the sheet so history + rollups stay fed.
    const prop = await getActiveProperty(db);
    const prefOff = prop != null && (prop.ask_expense_cost ?? 1) === 0;
    const habitual =
      task.tier === 'weekly' ||
      (task.difficulty !== 'moderate' &&
        task.difficulty !== 'hard' &&
        (task.time_minutes ?? 10) <= 15);
    if (prefOff || habitual) {
      await completeTask(db, task.id);
      await cancelTaskReminder(task.id);
      goBackOrTo(router, '/(tabs)');
      return;
    }
    router.push(`/complete/${task.id}` as never);
  };

  const onUnmark = async () => {
    if (!task || working) return;
    setWorking(true);
    try {
      await uncompleteTask(db, task.id);
      // Reload to reflect the new state inline (in case the user wants to
      // keep reading the steps); they can also back out and the source list
      // will refresh on focus.
      const fresh = await getTaskById(db, task.id);
      setTask(fresh);
    } finally {
      setWorking(false);
    }
  };

  const onSkip = async () => {
    if (!task || working) return;
    setWorking(true);
    try {
      await skipTask(db, task.id);
      await cancelTaskReminder(task.id);
      goBackOrTo(router, '/(tabs)');
    } finally {
      setWorking(false);
    }
  };

  const onDelete = async () => {
    if (!task || working) return;
    const ok = await confirm({
      title: 'Delete this task?',
      message: task.is_custom
        ? 'This removes your custom task from the planner. Past completions stay in your history.'
        : 'This removes the task from your planner. Run Replan year in Settings to bring it back if you change your mind.',
      confirmLabel: 'Delete',
      tone: 'destructive',
    });
    if (!ok) return;
    setWorking(true);
    try {
      await deleteTask(db, task.id);
      await cancelTaskReminder(task.id);
      goBackOrTo(router, '/(tabs)');
    } finally {
      setWorking(false);
    }
  };

  const onReschedule = () => setRescheduling(true);

  const confirmReschedule = async (days: number) => {
    if (!task) return;
    setWorking(true);
    try {
      await snoozeTask(db, task.id, days);
      // Cancel the OS reminder — its old fire time is now stale.
      await cancelTaskReminder(task.id);
      setRescheduling(false);
      goBackOrTo(router, '/(tabs)');
    } finally {
      setWorking(false);
    }
  };

  const onReminderPick = async (lead: 0 | 1 | 7) => {
    if (!task || reminderBusy) return;
    setReminderBusy(true);
    try {
      if (lead === 0) {
        await cancelTaskReminder(task.id);
        // Clear the persisted columns on both platforms — native already
        // unscheduled via cancelTaskReminder, web reads these columns
        // for the focus-checker so wiping them stops future delivery.
        await db.runAsync(
          `UPDATE task SET reminder_at = NULL, reminder_lead_days = NULL,
                           reminder_notified_at = NULL, updated_at = ?
             WHERE id = ?`,
          Date.now(),
          task.id
        );
        setReminderLead(0);
      } else if (task.due_at != null) {
        // Honor the global Task-reminders toggle in Settings →
        // Notifications. If the user has turned that off, we don't
        // schedule a new reminder even when they pick a lead on this
        // page. Existing per-task UI still works — they can re-enable
        // globally and tap again to schedule.
        const prop = await getActiveProperty(db);
        if (prop && (prop.notify_task_reminders ?? 1) === 0) {
          setReminderLead(0);
          void notice({
            title: 'Task reminders are paused',
            message:
              'Turn them back on in Settings → Notifications, then set this reminder again.',
            tone: 'warning',
          });
          return;
        }
        // Pass propertyId so the tap handler can auto-switch active home
        // before deep-linking to the task. Multi-home: tapping a reminder
        // for a task on the non-active home should switch home + open
        // the task, not crash on a property-mismatch.
        const id = await scheduleTaskReminder(
          task.id,
          task.title,
          task.due_at,
          lead,
          task.property_id
        );
        if (id) {
          // Persist on both platforms. Native uses the OS notification
          // for delivery (these columns are bookkeeping); web uses
          // them as the source of truth for the focus-checker.
          const fireAt = task.due_at - lead * 24 * 60 * 60 * 1000;
          await db.runAsync(
            `UPDATE task SET reminder_at = ?, reminder_lead_days = ?,
                             reminder_notified_at = NULL, updated_at = ?
               WHERE id = ?`,
            fireAt,
            lead,
            Date.now(),
            task.id
          );
          setReminderLead(lead);
        } else {
          // scheduleTaskReminder returns null when the fire time is
          // already past or the OS refused permission. Without this,
          // the chip just springs back with zero explanation.
          const fireAt = task.due_at - lead * 24 * 60 * 60 * 1000;
          const inPast = fireAt <= Date.now();
          void notice({
            title: "Couldn't set that reminder",
            message: inPast
              ? `${lead === 7 ? 'A week' : 'A day'} before this due date is already in the past. Pick a shorter lead or reschedule the task first.`
              : 'Notification permission is off. Enable it in Settings → Notifications and try again.',
            tone: 'warning',
          });
        }
      }
    } finally {
      setReminderBusy(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <Stack.Screen options={{ headerShown: false }} />
      <View style={styles.topbar}>
        <Pressable
          accessibilityLabel="Back"
          onPress={() => goBackOrTo(router, '/(tabs)')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <View style={{ flex: 1 }} />
        {/* Quick-escape shortcuts — dismiss any stacked task details so
            tapping back-then-back-then-back to climb out of a deep
            chain is no longer the only way home. Prev/next chrono nav
            now lives in a dedicated bottom strip (see below) — having
            it next to the back arrow at the top read as three
            confusing left-arrow buttons in a row. */}
        <View style={styles.topbarShortcuts}>
          {/* Edit task — only on active (uncompleted) tasks. Completed
              tasks shouldn't be re-titled retroactively (it would
              rewrite history in the timeline). */}
          {!done ? (
            <Pressable
              accessibilityLabel="Edit task"
              onPress={() => setEditing(true)}
              style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
              hitSlop={12}>
              <Pencil size={17} color={Palette.forest} strokeWidth={1.9} />
            </Pressable>
          ) : null}
          <Pressable
            accessibilityLabel="Go to Home"
            onPress={() => goTo(router, '/(tabs)')}
            style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <HomeIcon size={18} color={Palette.forest} strokeWidth={1.9} />
          </Pressable>
          <Pressable
            accessibilityLabel="Go to Schedule"
            onPress={() => goTo(router, '/(tabs)/schedule')}
            style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <Calendar size={18} color={Palette.forest} strokeWidth={1.9} />
          </Pressable>
        </View>
      </View>

      {/* Prev/next chronological nav — relocated from the topbar to a
          dedicated horizontal strip. Centered "Task X of Y" with arrows
          flanking it. Sits right under the topbar so it stays
          thumb-reachable on mobile (top of the screen → swipe down with
          thumb) AND visually balanced on desktop (matches the topbar's
          left-back / right-shortcut symmetry). Disabled when the
          current task isn't in the active list. */}
      {currentSiblingIdx >= 0 ? (
        <View style={styles.chronoNav}>
          <Pressable
            accessibilityLabel={prevSibling ? `Previous task: ${prevSibling.title}` : 'No previous task'}
            disabled={!prevSibling}
            onPress={() => prevSibling && router.replace(`/task/${prevSibling.id}` as never)}
            style={({ pressed }) => [
              styles.chronoBtn,
              !prevSibling && styles.chronoBtnDisabled,
              pressed && prevSibling && { opacity: 0.65 },
            ]}
            hitSlop={6}>
            <ChevronLeft
              size={16}
              color={prevSibling ? Palette.forest : Palette.inkFaint}
              strokeWidth={2.2}
            />
            <Text style={[styles.chronoBtnText, !prevSibling && styles.chronoBtnTextDisabled]}>
              Prev
            </Text>
          </Pressable>
          <Text style={styles.chronoCount}>
            Task {currentSiblingIdx + 1} of {siblings.length}
          </Text>
          <Pressable
            accessibilityLabel={nextSibling ? `Next task: ${nextSibling.title}` : 'No next task'}
            disabled={!nextSibling}
            onPress={() => nextSibling && router.replace(`/task/${nextSibling.id}` as never)}
            style={({ pressed }) => [
              styles.chronoBtn,
              !nextSibling && styles.chronoBtnDisabled,
              pressed && nextSibling && { opacity: 0.65 },
            ]}
            hitSlop={6}>
            <Text style={[styles.chronoBtnText, !nextSibling && styles.chronoBtnTextDisabled]}>
              Next
            </Text>
            <ChevronRight
              size={16}
              color={nextSibling ? Palette.forest : Palette.inkFaint}
              strokeWidth={2.2}
            />
          </Pressable>
        </View>
      ) : null}

      <ScrollView ref={pageScrollRef} contentContainerStyle={{ paddingBottom: 140 }} showsVerticalScrollIndicator={false}>
        <Cascade key={`${task.id}-c0`} order={0}>
        <View style={styles.heroWrap}>
          <LinearGradient
            colors={[Palette.forestLight, Palette.forest]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.hero}>
            <View style={styles.eyebrow}>
              <View style={[styles.dot, done && styles.dotDone]} />
              <Text style={styles.eyebrowText}>
                {done && task.completed_at
                  ? `COMPLETED · ${new Date(task.completed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : `${dueLabel(task.due_at).toUpperCase()}${task.tier ? ` · ${TIER_LABEL[task.tier]?.toUpperCase() ?? task.tier.toUpperCase()}` : ''}`}
              </Text>
            </View>

            <View style={styles.heroIcon}>
              <Icon size={26} color={Palette.cream} strokeWidth={1.7} />
            </View>

            <Text style={styles.title}>{task.title}</Text>

            <View style={styles.metaRow}>
              <Pill icon={<Clock size={12} color={Palette.cream} strokeWidth={1.6} />}>
                {task.time_minutes ?? 10} min
              </Pill>
              <Pill
                tone={task.difficulty === 'moderate' ? 'moderate' : task.difficulty === 'hard' ? 'hard' : 'easy'}>
                {task.difficulty === 'hard'
                  ? 'Hard'
                  : task.difficulty === 'moderate'
                  ? 'Moderate'
                  : 'Easy'}
              </Pill>
              {system ? (
                <Pill>
                  {system.label}
                  {subtype ? ` · ${subtype.label}` : ''}
                </Pill>
              ) : null}
            </View>
          </LinearGradient>
        </View>
        </Cascade>

        {task.why ? (
          <Cascade key={`${task.id}-c1`} order={1}>
          <Section icon={Info} title="Why this matters">
            <Text style={styles.body}>{task.why}</Text>
          </Section>
          </Cascade>
        ) : null}

        {(() => {
          // Section gate: show "Remind me" whenever a delivery path is
          // possible (native OS scheduler or web service-worker focus
          // fallback). On web we depend on the user opening the app on
          // or after the reminder date; we surface a hint to that
          // effect in the body copy so expectations match reality.
          const canRemind = NOTIFICATIONS_AVAILABLE || WEB_NOTIFICATIONS_AVAILABLE;
          if (done || !canRemind || task.due_at == null) return null;
          // Past due → reminders are nonsensical. Grey the section out
          // visually and disable interaction; the user can still see
          // there's a Remind-me feature but it's locked until they
          // reschedule the task.
          const past = task.due_at <= Date.now();
          // 1-week lead is only useful if the due date is at least a
          // week away. Same idea for 1-day: must be at least a day
          // out. We disable those chips selectively so the user gets
          // a clear hint about which leads are feasible.
          const oneDayMs = 24 * 60 * 60 * 1000;
          const oneDayOk = !past && task.due_at - oneDayMs > Date.now();
          const oneWeekOk = !past && task.due_at - 7 * oneDayMs > Date.now();
          return (
            <Cascade key={`${task.id}-c2`} order={2}>
            <Section icon={reminderLead > 0 ? Bell : BellOff} title="Remind me">
              <Text style={styles.body}>
                {past
                  ? "This task is already past due — reschedule it first, then come back to set a reminder."
                  : reminderLead === 0
                  ? 'No reminder set. Tap a window below to be pinged ahead of the due date.'
                  : reminderLead === 1
                  ? 'Pinging you 1 day before this is due.'
                  : 'Pinging you 1 week before this is due.'}
              </Text>
              <View style={styles.reminderRow}>
                <Pressable
                  onPress={() => onReminderPick(0)}
                  disabled={reminderBusy || past}
                  style={({ pressed }) => [
                    styles.reminderChip,
                    reminderLead === 0 && styles.reminderChipSel,
                    pressed && !past && { opacity: 0.85 },
                    past && { opacity: 0.35 },
                  ]}>
                  <Text
                    style={[
                      styles.reminderChipText,
                      reminderLead === 0 && styles.reminderChipTextSel,
                    ]}>
                    Off
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => onReminderPick(1)}
                  disabled={reminderBusy || !oneDayOk}
                  style={({ pressed }) => [
                    styles.reminderChip,
                    reminderLead === 1 && styles.reminderChipSel,
                    pressed && oneDayOk && { opacity: 0.85 },
                    !oneDayOk && { opacity: 0.35 },
                  ]}>
                  <Text
                    style={[
                      styles.reminderChipText,
                      reminderLead === 1 && styles.reminderChipTextSel,
                    ]}>
                    1 day before
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => onReminderPick(7)}
                  disabled={reminderBusy || !oneWeekOk}
                  style={({ pressed }) => [
                    styles.reminderChip,
                    reminderLead === 7 && styles.reminderChipSel,
                    pressed && oneWeekOk && { opacity: 0.85 },
                    !oneWeekOk && { opacity: 0.35 },
                  ]}>
                  <Text
                    style={[
                      styles.reminderChipText,
                      reminderLead === 7 && styles.reminderChipTextSel,
                    ]}>
                    1 week before
                  </Text>
                </Pressable>
              </View>
              {!past && Platform.OS === 'web' ? (
                <Text style={styles.webReminderNote}>
                  Heads up — on web we ping you when you next open
                  Homestead on or after the reminder date. Install the
                  iOS or Android app for guaranteed delivery.
                </Text>
              ) : null}
            </Section>
            </Cascade>
          );
        })()}

        {rule?.supplies && rule.supplies.length > 0 ? (
          <Cascade key={`${task.id}-c3`} order={3}>
          <Section icon={ShoppingCart} title="What you'll need">
            {rule.supplies.map((s, i) => (
              <Bullet key={i}>{s}</Bullet>
            ))}
          </Section>
          </Cascade>
        ) : null}

        {rule?.steps && rule.steps.length > 0 ? (
          <Cascade key={`${task.id}-c4`} order={4}>
          <Section icon={ListChecks} title="How to do it">
            {rule.steps.map((s, i) => (
              <Step key={i} n={i + 1}>
                {s}
              </Step>
            ))}
          </Section>
          </Cascade>
        ) : null}

        {rule?.tips && rule.tips.length > 0 ? (
          <Cascade key={`${task.id}-c5`} order={5}>
          <Section icon={Lightbulb} title="Pro tips" accent>
            {rule.tips.map((s, i) => (
              <Bullet key={i} accent>
                {s}
              </Bullet>
            ))}
          </Section>
          </Cascade>
        ) : null}

        {/* History (Wave 64, user ask): every recorded outcome of this
            task's rule on this home — done vs. skipped, with when, cost
            and vendor when logged. Answers "did I actually do this last
            year?" right where the decision is being made. */}
        <Cascade key={`${task.id}-c6`} order={6}>
        <Section icon={Clock} title="History">
          {history.length === 0 ? (
            <Text style={styles.historyEmpty}>
              First time on the books — no history for this one yet.
              Done and skipped outcomes will stack up here year over
              year.
            </Text>
          ) : (
            history.map((h, i) => {
              const when = new Date(h.completed_at);
              const days = Math.round((Date.now() - h.completed_at) / 86_400_000);
              const ago =
                days <= 0 ? 'today'
                : days === 1 ? 'yesterday'
                : days < 30 ? `${days} days ago`
                : days < 365 ? `${Math.round(days / 30)} mo ago`
                : `${Math.round(days / 365)} yr ago`;
              const meta = [
                h.vendor,
                h.cost_cents != null && h.cost_cents > 0
                  ? `$${(h.cost_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                  : null,
                h.notes,
              ].filter(Boolean).join(' · ');
              return (
                <View
                  key={`${h.completed_at}-${i}`}
                  style={[styles.historyRow, i < history.length - 1 && styles.historyRowBorder]}>
                  <View style={[styles.historyBadge, h.kind === 'skip' && styles.historyBadgeSkip]}>
                    {h.kind === 'skip' ? (
                      <SkipForward size={12} color={Palette.cream} strokeWidth={2.2} />
                    ) : (
                      <Check size={12} color={Palette.cream} strokeWidth={2.4} />
                    )}
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.historyLine}>
                      {h.kind === 'skip' ? 'Skipped' : 'Done'}
                      <Text style={styles.historyDate}>
                        {'  ·  '}
                        {when.toLocaleDateString(undefined, {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                        {'  ·  '}{ago}
                      </Text>
                    </Text>
                    {meta ? (
                      <Text style={styles.historyMeta} numberOfLines={2}>{meta}</Text>
                    ) : null}
                  </View>
                </View>
              );
            })
          )}
        </Section>
        </Cascade>

        <Pressable
          onPress={onDelete}
          disabled={working}
          accessibilityLabel="Delete this task"
          style={({ pressed }) => [styles.deleteBtn, pressed && { opacity: 0.7 }]}>
          <Trash2 size={14} color={Palette.clay} strokeWidth={1.8} />
          <Text style={styles.deleteText}>Delete task</Text>
        </Pressable>

        <Text style={styles.disclaimer}>
          Homestead suggestions are general home-care guidance. For complex repairs or
          anything involving gas, electrical, structural, or safety systems — please
          consult a licensed pro.
        </Text>
      </ScrollView>

      {/* Sticky action bar — Mark Done / Skip / Reschedule pinned to the
          bottom so the user never has to scroll to act. Completed-task
          view swaps to "Mark not done" + completion stamp. Delete stays
          inline in the scroll body (rare destructive action). */}
      <StickyBottomBar>
        {done ? (
          <>
            <View style={styles.doneBanner}>
              <CheckCircle2 size={18} color={Palette.moss} strokeWidth={2} />
              <Text style={styles.doneBannerText}>
                Completed
                {task.completed_at
                  ? ` ${new Date(task.completed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                  : ''}
              </Text>
            </View>
            <Pressable
              disabled={working}
              onPress={onUnmark}
              accessibilityLabel="Mark not done"
              style={({ pressed }) => [styles.btnUndo, pressed && { transform: [{ scale: 0.97 }] }]}>
              <RotateCcw size={16} color={Palette.forest} strokeWidth={2} />
              <Text style={styles.btnUndoText}>Mark not done</Text>
            </Pressable>
          </>
        ) : (
          <>
            <Pressable
              disabled={working}
              onPress={onMarkDone}
              style={({ pressed }) => [styles.btnDone, { flex: 1 }, pressed && { transform: [{ scale: 0.97 }] }]}>
              <Check size={18} color="#3A2A0C" strokeWidth={2.4} />
              <Text style={styles.btnDoneText}>Mark Done</Text>
            </Pressable>
            <Pressable
              disabled={working}
              onPress={onSkip}
              accessibilityLabel="Skip this one"
              style={({ pressed }) => [styles.btnSkipDetail, pressed && { transform: [{ scale: 0.94 }] }]}>
              <SkipForward size={18} color={Palette.clay} strokeWidth={1.9} />
            </Pressable>
            <Pressable
              disabled={working}
              onPress={onReschedule}
              accessibilityLabel="Reschedule"
              style={({ pressed }) => [styles.btnSnooze, pressed && { transform: [{ scale: 0.94 }] }]}>
              <AlarmClock size={19} color={Palette.ink} strokeWidth={1.7} />
            </Pressable>
          </>
        )}
      </StickyBottomBar>

      <ReschedulePicker
        task={rescheduling ? { id: task.id, title: task.title } : null}
        onCancel={() => setRescheduling(false)}
        onPick={confirmReschedule}
      />

      <TaskEditModal
        visible={editing}
        initial={{
          title: task.title,
          timeMinutes: task.time_minutes,
          difficulty: task.difficulty,
        }}
        onClose={() => setEditing(false)}
        onSave={async (next) => {
          await editTaskOverride(db, task.id, {
            title: next.title,
            time_minutes: next.timeMinutes,
            difficulty: next.difficulty,
          });
          // Re-fetch so the hero card reflects the new title/time.
          const fresh = await getTaskById(db, task.id);
          if (fresh) setTask(fresh);
        }}
      />
    </SafeAreaView>
  );
}

function Pill({
  children,
  icon,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  tone?: 'neutral' | 'easy' | 'moderate' | 'hard';
}) {
  const toneStyle =
    tone === 'easy'
      ? styles.pillEasy
      : tone === 'moderate'
      ? styles.pillModerate
      : tone === 'hard'
      ? styles.pillHard
      : null;
  const toneText =
    tone === 'easy'
      ? '#D4E8CC'
      : tone === 'moderate'
      ? '#F0CDA8'
      : tone === 'hard'
      ? '#F5C0B5'
      : Palette.cream;
  return (
    <View style={[styles.pill, toneStyle]}>
      {icon ?? null}
      <Text style={[styles.pillText, { color: toneText }]}>{children}</Text>
    </View>
  );
}

function Section({
  icon: SecIcon,
  title,
  children,
  accent,
}: {
  icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  title: string;
  children: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <View style={[styles.section, accent && styles.sectionAccent]}>
      <View style={styles.sectionHead}>
        <SecIcon
          size={16}
          color={accent ? Palette.honeyDeep : Palette.forest}
          strokeWidth={1.8}
        />
        <Text style={[styles.sectionTitle, accent && { color: Palette.honeyDeep }]}>
          {title}
        </Text>
      </View>
      <View>{children}</View>
    </View>
  );
}

function Bullet({ children, accent }: { children: React.ReactNode; accent?: boolean }) {
  return (
    <View style={styles.bulletRow}>
      <View style={[styles.bulletDot, accent && { backgroundColor: Palette.honeyDeep }]} />
      <Text style={[styles.bulletText, accent && { color: '#5A4012' }]}>{children}</Text>
    </View>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <View style={styles.stepRow}>
      <View style={styles.stepNum}>
        <Text style={styles.stepNumText}>{n}</Text>
      </View>
      <Text style={styles.stepText}>{children}</Text>
    </View>
  );
}

/**
 * Staggered top-down module reveal (Wave 71, user ask): each content
 * block fades in with a slight rise, 55ms apart, whenever the task id
 * changes — Prev/Next paging reads as a deliberate cascade instead of
 * a hard content swap. Keyed remounts retrigger the entering animation.
 */
function Cascade({ order, children }: { order: number; children: React.ReactNode }) {
  return (
    <Animated.View entering={FadeInDown.duration(240).delay(order * 55)}>
      {children}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Palette.sand,
  },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
  },
  back: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Quick-escape pair next to the back chevron. Smaller than back so the
  // chevron stays the primary visual cue, but always present so a user
  // who's drilled into many task details has a one-tap exit.
  // Centered prev/next strip below the topbar. Layout: text-labeled
  // pill on each side ("← Prev" / "Next →") with the count centered
  // between. Reads as a clear "navigate the task list" affordance
  // without competing with the back arrow.
  chronoNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.two,
    gap: Spacing.three,
  },
  chronoBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: Spacing.three,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    minWidth: 84,
    justifyContent: 'center',
  },
  chronoBtnDisabled: { opacity: 0.4 },
  chronoBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.forest,
    letterSpacing: 0.2,
  },
  chronoBtnTextDisabled: { color: Palette.inkFaint },
  chronoCount: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  topbarShortcuts: {
    flexDirection: 'row',
    gap: 6,
  },
  shortcutBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroWrap: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
  },
  hero: {
    borderRadius: Radius['3xl'],
    padding: Spacing.five,
    overflow: 'hidden',
  },
  eyebrow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Palette.honey,
  },
  eyebrowText: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    letterSpacing: 1.1,
    color: Palette.honeySoft,
  },
  heroIcon: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 50,
    height: 50,
    borderRadius: 15,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 26,
    lineHeight: 32,
    color: Palette.cream,
    letterSpacing: -0.4,
    marginTop: 16,
    paddingRight: 56,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 15,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 11,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.07)',
  },
  pillText: {
    fontSize: 13,
    fontFamily: Fonts.sansSemibold,
  },
  pillEasy: { backgroundColor: 'rgba(123,168,120,0.28)' },
  pillModerate: { backgroundColor: 'rgba(196,123,75,0.3)' },
  pillHard: { backgroundColor: 'rgba(176,95,60,0.35)' },

  section: {
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  sectionAccent: {
    backgroundColor: Palette.honeySoft,
    borderColor: '#E7CC91',
  },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginBottom: Spacing.three,
  },
  sectionTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.forest,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  body: {
    fontFamily: Fonts.sans,
    fontSize: 14,
    lineHeight: 22,
    color: Palette.ink,
  },
  bulletRow: {
    flexDirection: 'row',
    gap: 10,
    paddingVertical: 5,
    alignItems: 'flex-start',
  },
  bulletDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Palette.forest,
    marginTop: 8,
  },
  bulletText: {
    flex: 1,
    fontFamily: Fonts.sans,
    fontSize: 14,
    lineHeight: 21,
    color: Palette.ink,
  },
  stepRow: {
    flexDirection: 'row',
    gap: 11,
    paddingVertical: 7,
    alignItems: 'flex-start',
  },
  stepNum: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Palette.forest,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  stepNumText: {
    color: Palette.cream,
    fontFamily: Fonts.serifBold,
    fontSize: 13,
  },
  stepText: {
    flex: 1,
    fontFamily: Fonts.sans,
    fontSize: 14,
    lineHeight: 22,
    color: Palette.ink,
  },
  actions: {
    flexDirection: 'row',
    gap: Spacing.two,
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
  },
  btnDone: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    backgroundColor: Palette.honey,
    paddingVertical: 16,
    borderRadius: 18,
  },
  btnDoneText: {
    color: '#3A2A0C',
    fontFamily: Fonts.sansBold,
    fontSize: 15.5,
  },
  // Same vertical padding as btnDone so the trio is a perfectly even
  // row — the icon-only buttons used to collapse to ~36px tall while
  // Mark Done was 50px, making the bar look unbalanced.
  btnSnooze: {
    width: 56,
    alignSelf: 'stretch',
    paddingVertical: 16,
    borderRadius: 18,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnSkipDetail: {
    width: 56,
    alignSelf: 'stretch',
    paddingVertical: 16,
    borderRadius: 18,
    // Clay-tinted background so the skip button reads as the "muted"
    // alternative to the bright honey Mark Done. Same footprint as the
    // reschedule icon button.
    backgroundColor: 'rgba(176,95,60,0.13)',
    borderWidth: 1,
    borderColor: 'rgba(176,95,60,0.25)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotDone: {
    backgroundColor: Palette.moss,
  },
  doneBanner: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    backgroundColor: '#E0EAD9',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#C0D4B6',
  },
  doneBannerText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: '#33532F',
  },
  btnUndo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  btnUndoText: {
    color: Palette.forest,
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
  },
  historyEmpty: {
    fontFamily: Fonts.sansMedium, fontSize: 13.5, color: Palette.inkSoft,
    lineHeight: 18,
  },
  historyRow: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 10,
    paddingVertical: 9,
  },
  historyRowBorder: { borderBottomWidth: 1, borderBottomColor: Palette.line },
  historyBadge: {
    width: 22, height: 22, borderRadius: 7,
    backgroundColor: Palette.moss,
    alignItems: 'center', justifyContent: 'center',
    marginTop: 1,
  },
  historyBadgeSkip: { backgroundColor: Palette.clay },
  historyLine: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  historyDate: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
  },
  historyMeta: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    lineHeight: 16,
  },
  deleteBtn: {
    marginTop: Spacing.four,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(176,95,60,0.25)',
    backgroundColor: 'rgba(176,95,60,0.06)',
  },
  deleteText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.clay,
  },
  disclaimer: {
    marginTop: Spacing.five,
    marginHorizontal: Spacing.four,
    fontSize: 12.5,
    lineHeight: 17,
    color: Palette.inkFaint,
    fontFamily: Fonts.sansMedium,
    textAlign: 'center',
  },
  reminderRow: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10,
  },
  reminderChip: {
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999,
    borderWidth: 1.5, borderColor: Palette.line, backgroundColor: Palette.card,
  },
  reminderChipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  reminderChipText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  // Caveat shown on web — best-effort delivery vs guaranteed OS push.
  webReminderNote: {
    marginTop: 10,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkFaint,
    lineHeight: 16,
    fontStyle: 'italic',
  },
  reminderChipTextSel: { color: Palette.cream },
});
